<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, h, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  NCard, NTabs, NTabPane, NSpace, NButton, NDataTable, NText, NEmpty,
  NPopconfirm, NTag, NLog, NAlert,
  type DataTableColumns,
} from 'naive-ui'
import {
  services, versions, deployments, streams,
  type ServiceOperation, type Version, type Deployment,
} from '../api'
import { useEvents } from '../composables/useEvents'
import { message } from '../message'
import { createServiceDetailStore } from '../stores/serviceDetail'
import OpProgress, { type OpView, type OpKind, type OpStatus } from '../components/OpProgress.vue'
import { toOperationView } from '../utils/operation'
import {
  deriveOverviewOperationFromDeployPhase,
  deriveOverviewOperationFromLifecycleEvent,
} from './serviceDetail.operation'

const route = useRoute()
const router = useRouter()
const id = computed(() => Number(route.params.id))

// Store delegates to services.overviewGet(id.value), versions.list(id.value), and deployments.list(id.value).
const store = createServiceDetailStore({ services, versions, deployments })
const { overview, versions: vers, deployments: deps, service: svc, health } = store
// const overview = ref<ServiceOverview | null>(null)
const tab = ref<string>('versions')
const pendingAction = ref<string | null>(null)
const opView = computed(() => overview.value ? toOperationView(overview.value.operation) : null)
const busy = computed(() =>
  pendingAction.value !== null ||
  !!opView.value?.busy,
)
const busyTitle = computed(() => busy.value ? '该服务正在执行其它操作，请等待完成' : '')

const containerState = computed(() => health.value?.container_state || 'unknown')
const hasPrevSuccess = computed(() =>
  deps.value.some(d => d.status === 'success' && d.version_id !== svc.value?.current_version_id)
)
const visibleSetFrozen = ref<'start' | 'stoprestart' | null>(null)

function setOverviewOperation(next: ServiceOperation) {
  if (!overview.value) return
  overview.value = { ...overview.value, operation: next }
}

function setOverviewLifecycleOperation(payload: Record<string, any>) {
  const next = deriveOverviewOperationFromLifecycleEvent(overview.value, payload)
  if (!next) return
  setOverviewOperation(next)
}

const currentOp = computed<OpView | null>(() => {
  if (!overview.value || !opView.value?.busy) return null
  return {
    kind: overview.value.operation.kind as OpKind,
    id: overview.value.operation.deployment_id,
    phase: overview.value.operation.phase || '',
    status: overview.value.operation.status as OpStatus,
    error: overview.value.operation.error,
    statusText: opView.value.statusText,
    startedAt: overview.value.operation.started_at || 0,
    endedAt: overview.value.operation.ended_at,
  }
})

async function load() {
  try {
    await store.load(id.value)
  } catch (e: any) {
    message.error(e.message)
  }
}

useEvents({
  'deploy.started':     (_ev) => {
    // A new deployment was created — refresh so deps.value picks it up.
    void load()
  },
  'lifecycle.started':  (ev) => {
    const p = (ev.payload ?? {}) as Record<string, any>
    setOverviewLifecycleOperation(p)
  },
  'lifecycle.phase':    (ev) => {
    const p = (ev.payload ?? {}) as Record<string, any>
    setOverviewLifecycleOperation(p)
  },
  'deploy.phase':       (ev) => {
    const p = (ev.payload ?? {}) as Record<string, any>
    const depId = p.deployment_id
    const phase = p.phase
    if (!depId || !phase) return
    const idx = deps.value.findIndex(d => d.id === depId)
    if (idx === -1) {
      // Unknown deployment — fetch to populate.
      void load()
      return
    }
    // Map pipeline phase keys onto deployment status values. Only the four
    // that the schema knows about become statuses; the rest (pre/proxy/post)
    // leave status alone but still feed the phase into the OpView via the
    // existing currentOp logic below if needed.
    if (phase === 'pulling' || phase === 'swapping' || phase === 'probing') {
      deps.value[idx] = { ...deps.value[idx], status: phase }
    }
    const next = deriveOverviewOperationFromDeployPhase(overview.value, deps.value, p)
    if (!next) return
    setOverviewOperation(next)
  },
  'deploy.success':     () => load(),
  'deploy.failed':      () => load(),
  'deploy.rolled_back': () => load(),
  'deploy.cancelled':   () => load(),
  'version.received':   () => load(),
  'health.up':          () => load(),
  'health.down':        () => load(),
  'lifecycle.done':     () => load(),
  'lifecycle.failed':   () => load(),
  'lifecycle.cancelled':() => load(),
})

onMounted(async () => {
  await load()
})
onBeforeUnmount(() => {
  closeLogs()
})

async function fireAction<T>(name: string, fn: () => Promise<T>, successMsg: string, onSuccess?: (result: T) => void) {
  if (pendingAction.value || busy.value) return
  // Freeze the Start/Stop/Restart visible-set so it doesn't flicker mid-op.
  visibleSetFrozen.value = (containerState.value === 'stopped') ? 'start' : 'stoprestart'
  pendingAction.value = name
  try {
    const result = await fn()
    message.success(successMsg)
    onSuccess?.(result)
  } catch (e: any) {
    message.error(e.message)
  } finally {
    pendingAction.value = null
  }
}

const doDeploy   = (vid: number) => fireAction('deploy', () => deployments.deploy(id.value, vid), '已开始部署', (d: Deployment) => {
  // Inject the new deployment at the head of deps so subsequent deploy.phase events find it
  // before the deploy.started SSE arrives (which comes only at the swapping phase).
  deps.value = [d, ...deps.value.filter(x => x.id !== d.id)]
  setOverviewOperation({
    kind: d.triggered_by === 'rollback' ? 'rollback' : 'deploy',
    status: 'running',
    phase: d.status,
    deployment_id: d.id,
    started_at: d.started_at,
    ended_at: d.finished_at,
    error: d.error,
  })
})
const doRollback = ()             => fireAction('rollback', () => deployments.rollback(id.value), '已开始回滚', (d: Deployment) => {
  deps.value = [d, ...deps.value.filter(x => x.id !== d.id)]
  setOverviewOperation({
    kind: 'rollback',
    status: 'running',
    phase: d.status,
    deployment_id: d.id,
    started_at: d.started_at,
    ended_at: d.finished_at,
    error: d.error,
  })
})
const doStart   = () => fireAction('start',   () => services.start(id.value),   '正在启动容器…', (op) => { setOverviewOperation({ ...op, phase: 'starting', status: 'running' }) })
const doStop    = () => fireAction('stop',    () => services.stop(id.value),    '正在停止容器…', (op) => { setOverviewOperation({ ...op, phase: 'stopping', status: 'running' }) })
const doRestart = () => fireAction('restart', () => services.restart(id.value), '正在重启容器…', (op) => { setOverviewOperation({ ...op, phase: 'stopping', status: 'running' }) })

async function doDelete() {
  try {
    await services.delete(id.value)
    message.success('deleted')
    router.push('/')
  } catch (e: any) { message.error(e.message) }
}

async function deleteVersion(vid: number) {
  try {
    await versions.delete(vid)
    message.success('version deleted')
    await load()
  } catch (e: any) { message.error(e.message) }
}

const logBuf = ref('')
let logSource: EventSource | null = null
function openLogs() {
  if (logSource) return
  logBuf.value = ''
  logSource = new EventSource(streams.serviceLogs(id.value))
  logSource.onmessage = (m) => { logBuf.value += m.data + '\n' }
  logSource.onerror = () => { logSource?.close(); logSource = null }
}
function closeLogs() {
  logSource?.close()
  logSource = null
}

const versionCols: DataTableColumns<Version> = [
  { title: 'Tag', key: 'tag' },
  { title: 'Digest', key: 'digest',
    render(v) { return v.digest.substring(0, 19) + '…' } },
  { title: 'Commit', key: 'commit_sha',
    render(v) { return (v.commit_sha || '').substring(0, 8) } },
  { title: 'Message', key: 'commit_msg' },
  {
    title: 'Pulled', key: 'pulled',
    render(v) { return h(NTag, { type: v.pulled ? 'success' : 'default', size: 'small' }, () => v.pulled ? 'yes' : 'no') },
  },
  { title: 'Size', key: 'size_bytes',
    render(v) { return v.size_bytes ? Math.round(v.size_bytes / (1024 * 1024)) + ' MB' : '—' } },
  {
    title: '', key: 'actions', width: 260,
    render(v) {
      const isCurrent = v.id === svc.value?.current_version_id
      const deployDisabled = busy.value || isCurrent
      const deployTitle = busy.value ? busyTitle.value
        : isCurrent ? '该版本已为当前线上版本' : ''
      const deleteDisabled = isCurrent
      const deleteTitle = isCurrent ? '无法删除当前线上版本' : ''
      return h(NSpace, { size: 'small' }, () => [
        h(NButton, {
          size: 'small', type: 'primary', disabled: deployDisabled,
          loading: pendingAction.value === 'deploy',
          title: deployTitle,
          onClick: () => !deployDisabled && doDeploy(v.id),
        }, () => 'Deploy'),
        h(NPopconfirm, {
          onPositiveClick: () => deleteVersion(v.id),
          disabled: deleteDisabled,
        }, {
          default: () => 'Delete version record?',
          trigger: () => h(NButton, {
            size: 'small', secondary: true, disabled: deleteDisabled, title: deleteTitle,
          }, () => 'Delete'),
        }),
      ])
    },
  },
]

const deployCols: DataTableColumns<Deployment> = [
  { title: 'ID', key: 'id', width: 60 },
  { title: 'Version', key: 'version_id', width: 90 },
  { title: 'Status', key: 'status',
    render(d) {
      const type = d.status === 'success' ? 'success'
        : d.status === 'failed' ? 'error'
        : d.status === 'cancelled' ? 'warning'
        : d.status === 'rolled_back' ? 'warning' : 'info'
      return h(NTag, { type, size: 'small' }, () => d.status)
    },
  },
  { title: 'By', key: 'triggered_by' },
  { title: 'Started', key: 'started_at',
    render(d) { return new Date(d.started_at * 1000).toLocaleString() } },
  {
    title: '', key: 'actions', width: 100,
    render(d) {
      return h(NButton, { size: 'small', onClick: () => router.push(`/deployments/${d.id}`) }, () => 'Log')
    },
  },
]

const healthColor = computed(() =>
  health.value?.container_state === 'running' ? '#18a058'
  : health.value?.container_state === 'stopped' ? '#d03050'
  : health.value?.container_state === 'missing' ? '#d03050'
  : health.value?.state === 'up' ? '#18a058'
  : health.value?.state === 'down' ? '#d03050'
  : '#bbb',
)

const showStart = computed(() =>
  busy.value
    ? visibleSetFrozen.value === 'start'
    : containerState.value === 'stopped',
)
const showStopRestart = computed(() =>
  busy.value
    ? visibleSetFrozen.value === 'stoprestart'
    : containerState.value === 'running',
)

watch(busy, (b) => {
  if (!b) visibleSetFrozen.value = null
})
</script>

<template>
  <div v-if="svc">
    <NCard>
      <NSpace align="center" justify="space-between" style="width:100%">
        <NSpace align="center">
          <span :style="`display:inline-block;width:12px;height:12px;border-radius:50%;background:${healthColor}`" />
          <NText style="font-size:18px;font-weight:600">{{ svc.name }}</NText>
          <NText depth="3">{{ svc.image_repo }}</NText>
          <NText v-if="svc.domain" depth="3">→ {{ svc.domain }}</NText>
          <NTag v-if="svc.current_version_id" type="info" size="small">v{{ svc.current_version_id }}</NTag>
        </NSpace>
        <NSpace>
          <NPopconfirm v-if="showStart" @positive-click="doStart" :disabled="busy">
            <template #trigger>
              <NButton size="small" :disabled="busy" :loading="pendingAction === 'start'" :title="busyTitle || '启动 docker 容器（不重新拉镜像）'">Start</NButton>
            </template>
            启动当前服务容器（不会拉新镜像，不触发部署流程）。继续？
          </NPopconfirm>
          <NPopconfirm v-if="showStopRestart" @positive-click="doStop" :disabled="busy">
            <template #trigger>
              <NButton size="small" :disabled="busy" :loading="pendingAction === 'stop'" :title="busyTitle || '停止容器但保留镜像与配置'">Stop</NButton>
            </template>
            停止服务容器（保留镜像与配置，可随时 Start 恢复）。继续？
          </NPopconfirm>
          <NPopconfirm v-if="showStopRestart" @positive-click="doRestart" :disabled="busy">
            <template #trigger>
              <NButton size="small" :disabled="busy" :loading="pendingAction === 'restart'" :title="busyTitle || '重启容器，用于让配置/环境变量变更生效'">Restart</NButton>
            </template>
            重启容器（让保存后的环境变量/配置变更生效）。继续？
          </NPopconfirm>
          <NPopconfirm v-if="hasPrevSuccess" @positive-click="doRollback" :disabled="busy">
            <template #trigger>
              <NButton size="small" type="warning" :disabled="busy" :loading="pendingAction === 'rollback'" :title="busyTitle || '切回上一次部署成功的版本'">Rollback</NButton>
            </template>
            回滚到上一次部署成功的版本？这会立刻替换正在运行的容器。
          </NPopconfirm>
          <NButton size="small" @click="router.push(`/services/${id}/onboarding`)" title="重新打开 GitHub workflow / secret 接入向导">GitHub Onboarding</NButton>
          <NButton size="small" @click="router.push(`/services/${id}/edit`)" title="修改服务元数据（保存后不会自动重启容器）">Edit</NButton>
          <NPopconfirm @positive-click="doDelete" :disabled="busy">
            <template #trigger>
              <NButton size="small" type="error" :disabled="busy" :title="busyTitle || '删除服务数据库记录及其关联历史'">Delete</NButton>
            </template>
            删除该服务？这会级联删除其所有版本与部署历史（数据库记录），并停止并删除容器、移除代理配置，操作不可逆。
          </NPopconfirm>
        </NSpace>
      </NSpace>
    </NCard>

    <OpProgress
      v-if="currentOp"
      :op="currentOp"
      :service="svc"
      @cancelled="load"
    />

    <NCard style="margin-top:16px">
      <NTabs v-model:value="tab" type="line" @update:value="(k: string) => k === 'logs' ? openLogs() : closeLogs()">
        <NTabPane name="versions" tab="版本 Versions">
          <NAlert type="default" style="margin-bottom:12px">
            CI 每次构建后通过 webhook 上报一条记录（tag + digest + commit）。<b>Deploy</b> 会拉取该镜像并替换当前容器；<b>Delete</b> 仅删除该版本记录。
          </NAlert>
          <NDataTable :columns="versionCols" :data="vers" :key="`${busy}-${pendingAction}`" />
          <NEmpty v-if="vers.length === 0" description="尚无版本。请确认 CI 已配置 webhook 上报。" />
        </NTabPane>
        <NTabPane name="deployments" tab="部署历史 Deployments">
          <NAlert type="default" style="margin-bottom:12px">
            每次「Deploy / Rollback」都会产生一条记录。状态：<code>success</code> 健康检查通过；<code>failed</code> 部署失败；<code>cancelled</code> 用户取消。点击「Log」可查看完整日志。
          </NAlert>
          <NDataTable :columns="deployCols" :data="deps" />
          <NEmpty v-if="deps.length === 0" description="尚无部署记录。" />
        </NTabPane>
        <NTabPane name="logs" tab="实时日志 Live logs">
          <NAlert type="default" style="margin-bottom:12px">
            从 docker 容器 stdout/stderr 通过 SSE 流式拉取最近的输出。切走 tab 后连接会自动关闭。
          </NAlert>
          <NLog :log="logBuf || '（等待容器输出…）'" :rows="20" trim />
        </NTabPane>
        <NTabPane name="settings" tab="配置 Settings">
          <NAlert type="default" style="margin-bottom:12px">
            当前服务在数据库中的完整记录（只读视图）。要修改请点击右上角「Edit」。
          </NAlert>
          <pre style="background:#fafafa;border:1px solid #eee;padding:12px;border-radius:4px;font-size:12px">{{ JSON.stringify(svc, null, 2) }}</pre>
        </NTabPane>
      </NTabs>
    </NCard>
  </div>
</template>
